// RunningTicker.js
// Drop-in replacement for the inline RunningTicker in HomeDashboard.js
// Fetches live quotes from Railway backend (which caches via PostgreSQL + Alpha Vantage)

import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// Symbols to show in the ticker
const TICKER_SYMBOLS = [
  'SPY', 'QQQ', 'DIA',
  'JPM', 'BAC', 'GS', 'MS', 'WFC', 'C', 'USB', 'PNC',
  'AAPL', 'NVDA', 'TSLA', 'MSFT',
  'BTC-USD', 'ETH-USD',
];

// Fallback static data shown while first fetch is in flight
const FALLBACK_DATA = TICKER_SYMBOLS.map(symbol => ({
  symbol,
  price:     null,
  changePct: 0,
  up:        true,
}));

const POLL_INTERVAL_MS = 60_000; // refresh every 60 seconds

const RunningTicker = () => {
  const [quotes, setQuotes]     = useState(FALLBACK_DATA);
  const [loading, setLoading]   = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);

  const fetchQuotes = async () => {
    try {
      const symbols = TICKER_SYMBOLS.join(',');
      const res  = await fetch(`${API_URL}/ticker/quotes?symbols=${symbols}`);
      const json = await res.json();

      if (json.success && json.data?.length > 0) {
        // Merge API results; keep fallback order for any missing symbols
        const map = {};
        for (const q of json.data) map[q.symbol] = q;

        setQuotes(
          TICKER_SYMBOLS.map(sym => map[sym] || { symbol: sym, price: null, changePct: 0, up: true })
        );
        setLastUpdate(new Date());
      }
    } catch (err) {
      console.error('❌ RunningTicker fetch error:', err);
      // Keep existing quotes on error — don't blank the ticker
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
    intervalRef.current = setInterval(fetchQuotes, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Double items for seamless infinite scroll
  const items = [...quotes, ...quotes];

  const formatPrice = (price) => {
    if (price === null) return '---';
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return price.toFixed(2);
  };

  const formatPct = (pct) => {
    if (pct === null || pct === undefined) return '0.00%';
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(2)}%`;
  };

  return (
    <div style={{
      width: '100%',
      background: 'rgba(10, 18, 35, 0.95)',
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      borderTop:    '1px solid rgba(255,255,255,0.07)',
      overflow: 'hidden',
      padding: '0.55rem 0',
      marginBottom: '1.5rem',
      position: 'relative',
    }}>

      {/* Fade edges */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: '60px',
        background: 'linear-gradient(to right, rgba(10,18,35,0.95), transparent)',
        zIndex: 2, pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: '60px',
        background: 'linear-gradient(to left, rgba(10,18,35,0.95), transparent)',
        zIndex: 2, pointerEvents: 'none'
      }} />

      {/* Live indicator */}
      {!loading && lastUpdate && (
        <div style={{
          position: 'absolute', right: '70px', top: '50%', transform: 'translateY(-50%)',
          zIndex: 3, display: 'flex', alignItems: 'center', gap: '0.3rem',
          fontSize: '0.65rem', color: '#64748b'
        }}>
          <span style={{
            width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e',
            boxShadow: '0 0 6px #22c55e', display: 'inline-block',
            animation: 'iq-ticker-pulse 2s ease-in-out infinite'
          }} />
          LIVE
        </div>
      )}

      <style>{`
        @keyframes iq-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes iq-ticker-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
        .iq-ticker-track {
          display: flex;
          width: max-content;
          animation: iq-ticker-scroll 50s linear infinite;
          will-change: transform;
        }
        .iq-ticker-track:hover { animation-play-state: paused; }
      `}</style>

      <div className="iq-ticker-track">
        {items.map((item, i) => (
          <div key={i} style={{
            display:        'inline-flex',
            alignItems:     'center',
            gap:            '0.45rem',
            padding:        '0 1.25rem',
            borderRight:    '1px solid rgba(255,255,255,0.06)',
            whiteSpace:     'nowrap',
            flexShrink:     0,
            opacity:        loading ? 0.4 : 1,
            transition:     'opacity 0.4s ease',
          }}>
            <span style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '0.8rem', letterSpacing: '0.03em' }}>
              {item.symbol}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
              {item.price !== null ? `$${formatPrice(item.price)}` : '---'}
            </span>
            <span style={{
              fontSize:   '0.75rem',
              fontWeight: '600',
              color:      item.up ? '#22c55e' : '#ef4444',
            }}>
              {item.up ? '▲' : '▼'} {formatPct(item.changePct)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RunningTicker;
